import React, { useEffect, useLayoutEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaHeart, FaRegHeart, FaShareAlt } from "react-icons/fa";

import CarImageCarousel from "./Components/CarImageCarousel";
import CarFeatureList from "./Components/CarFeatureList";
import CompModal from "./Components/CompModal";
import MestimateChart from "./Components/MestimateChart";
import RecentlyViewed from "./RecentlyViewed";
import Spinner from "./Components/Spinner";
import { useAuth } from "./AuthContext";
import ExplainThisPrice from "./Components/ExplainThisPrice";
import ContactAtMestimate from "./Components/ContactAtMestimate";
import BestDealsNearby from "./Components/BestDealsNearby";
import Skeleton from "./Components/Skeleton";
import { getVehicleCanonicalUrl, getVehiclePath } from "./Utils/vehicleUrls";

const usd = (n, digits = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : n;

const fmtInt = (n) =>
  typeof n === "number" ? n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : n;

const limitMetaDescription = (description, maximumLength = 158) => {
  if (description.length <= maximumLength) return description;
  const shortened = description.slice(0, maximumLength - 1).trimEnd();
  const lastSpace = shortened.lastIndexOf(" ");
  const boundary = lastSpace >= maximumLength - 24 ? lastSpace : shortened.length;
  return `${shortened.slice(0, boundary).replace(/[\s.,]+$/, "")}…`;
};

const buildVehicleMetaDescription = (car, title, dealerLocation) => limitMetaDescription(
  `${title} with ${fmtInt(car.mileage)} miles${Number.isFinite(car.price) ? `, listed at ${usd(car.price, 0)}` : ""}${dealerLocation ? ` in ${dealerLocation}` : ""}. Compare its price, dealer details, sold comparables, and market insights on Meshum.`
);

const getAbsoluteHttpUrl = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

const logCompsOpen = async (carId) => {
  try {
    await fetch(`${process.env.REACT_APP_API_BASE}/cars/${carId}/comps-open`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("buyerToken") || ""}`,
      },
    });
  } catch (e) {
    console.warn("Failed to log comps open", e);
  }
};

function CarDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [car, setCar] = useState(null);
  const [comparables, setComparables] = useState([]);
  const [comparablesLoading, setComparablesLoading] = useState(true);
  const [mestimate, setMestimate] = useState(null);
  const [compsUsed, setCompsUsed] = useState([]);
  const [mestimateStatus, setMestimateStatus] = useState(null);
  const [mestimateMessage, setMestimateMessage] = useState(null);
  const [mestimateRequestState, setMestimateRequestState] = useState("loading");
  const [carLoadState, setCarLoadState] = useState("loading");
  const [showCompModal, setShowCompModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllPrices, setShowAllPrices] = useState(false);

  const { user } = useAuth();
  const isLoggedIn = !!user?.id;

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState("");

  const showTempToast = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(""), 2000);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === "1") {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  useEffect(() => {
    if (!user?.id || !car) return;
    fetch(`${process.env.REACT_APP_API_BASE}/saved/${user.id}`)
      .then((res) => res.json())
      .then((ids) => setSaved(ids.includes(car.id)))
      .catch(() => {});
  }, [user, car]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    setCar(null);
    setComparables([]);
    setMestimate(null);
    setCompsUsed([]);
    setMestimateStatus(null);
    setMestimateMessage(null);
    setMestimateRequestState("loading");
    setCarLoadState("loading");
    setComparablesLoading(true);
    setShowCompModal(false);

    fetch(`${process.env.REACT_APP_API_BASE}/cars/${id}`)
      .then((res) => {
        if (res.status === 404) {
          const error = new Error("Car not found");
          error.status = 404;
          throw error;
        }
        if (!res.ok) {
          throw new Error("Unable to load car");
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCar(data);
        setCarLoadState("success");

        const stored = localStorage.getItem("recentlyViewedCars");
        let viewed = stored ? JSON.parse(stored) : [];
        viewed = viewed.filter((c) => String(c.id) !== String(data.id));
        viewed.unshift({
          id: data.id,
          make: data.make,
          model: data.model,
          trim: data.trim,
          year: data.year,
          price: data.price,
          dealerCity: data.dealerCity,
          dealerState: data.dealerState,
          images: data.images[0],
        });
        if (viewed.length > 5) viewed = viewed.slice(0, 5);
        localStorage.setItem("recentlyViewedCars", JSON.stringify(viewed));

        const sold = data?.isActive === false || !!data?.soldDate;
        if (sold) {
          setComparables([]);
          setMestimate(null);
          setCompsUsed([]);
          setMestimateStatus(null);
          setMestimateMessage(null);
          setMestimateRequestState("success");
          setComparablesLoading(false);
          return;
        }

        fetch(`${process.env.REACT_APP_API_BASE}/cars/${id}/comparables`)
          .then((res) => {
            if (!res.ok) {
              throw new Error(`Unable to load comparables (HTTP ${res.status})`);
            }
            return res.json();
          })
          .then((compData) => {
            if (cancelled) return;
            setComparables(compData.comps || []);
            setMestimate(typeof compData.mestimate === "number" ? compData.mestimate : null);
            setCompsUsed(compData.compsUsed ?? []);
            setMestimateStatus(compData.status ?? null);
            setMestimateMessage(compData.message ?? null);
            setMestimateRequestState("success");
            setComparablesLoading(false);
          })
          .catch(() => {
            if (cancelled) return;
            setComparables([]);
            setMestimate(null);
            setCompsUsed([]);
            setMestimateStatus(null);
            setMestimateMessage(null);
            setMestimateRequestState("error");
            setComparablesLoading(false);
          });
      })
      .catch((error) => {
        if (cancelled) return;
        setCarLoadState(error?.status === 404 ? "missing" : "error");
        setMestimateRequestState("error");
        setComparablesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!car) return;
    if (String(car.id) !== String(id)) return;
    const canonicalPath = getVehiclePath(car);
    if (canonicalPath && location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true, state: location.state ?? null });
    }
  }, [car, id, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (car) {
      window.gtag?.("event", "view_car_detail", {
        car_id: car.id,
        make: car.make,
        model: car.model,
        year: car.year,
        price: car.price,
        source_page: location.state?.from?.pathname === '/insights/best-deals' ? 'best_deals' : undefined,
      });
      window.fbq?.("track", "ViewContent", {
        content_name: `${car.year} ${car.make} ${car.model}`,
        content_ids: [car.id],
        content_type: "vehicle",
        value: car.price || 0,
        currency: "USD",
      });
    }
  }, [car, location.state]);

  useLayoutEffect(() => {
    if (!car) return;
    document.getElementById("seo-shell")?.remove();
  }, [car]);

  const toggleSave = async () => {
    if (!isLoggedIn) {
      showTempToast("Sign in to save this car");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const method = saved ? "DELETE" : "POST";
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/saved`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, carId: car.id }),
      });
      if (res.ok) setSaved(!saved);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const url = getVehicleCanonicalUrl(car, window.location.origin);
    if (navigator.share) {
      try {
        await navigator.share({ title: `${car.year} ${car.make} ${car.model}`, url });
      } catch {}
    } else {
      navigator.clipboard
        .writeText(url)
        .then(() => showTempToast("Link copied to clipboard!"))
        .catch(() => showTempToast("Failed to copy link"));
    }
  };

  if (!car) {
    if (carLoadState === "missing") {
      return (
        <div className="car-detail-page">
          <div className="car-card">
            <h1>Vehicle not found</h1>
            <p>This vehicle may no longer be available or the link may be incorrect.</p>
            <Link className="btn primary" to="/search">Search available cars</Link>
          </div>
        </div>
      );
    }

    if (carLoadState === "error") {
      return (
        <div className="car-detail-page">
          <div className="car-card">
            <h1>Unable to load vehicle details</h1>
            <p>Please refresh the page or return to search.</p>
            <Link className="btn primary" to="/search">Back to search</Link>
          </div>
        </div>
      );
    }

    return <Spinner />;
  }

  const backHref =
    location.state?.from
      ? `${location.state.from.pathname}${location.state.from.search || ""}`
      : localStorage.getItem("lastSearchURL") || "/search";

  const rawFeatures = car.features ?? "";
  const title = [car.year, car.make, car.model, car.trim].filter(Boolean).join(" ");
  const canonicalUrl = getVehicleCanonicalUrl(car);
  const dealerLocation = [car.dealerCity, car.dealerState].filter(Boolean).join(", ");
  const metaDescription = buildVehicleMetaDescription(car, title, dealerLocation);
  const isSold = car.isActive === false || !!car.soldDate;
  const insightComps = (compsUsed?.length ? compsUsed : comparables) || [];
  const estimateValue = typeof mestimate === "number" ? mestimate : null;
  const isMestimateLoading = !isSold && mestimateRequestState === "loading";
  const hasMestimateError = !isSold && mestimateRequestState === "error";
  const estimateUnavailable = !isSold && mestimateRequestState === "success" && estimateValue == null;
  const estimateUnavailableMessage =
    estimateUnavailable && mestimateMessage
      ? mestimateMessage
      : estimateUnavailable && mestimateStatus
        ? "Comparable sales are still too thin to estimate value."
      : "Comparable sales are still too thin to estimate value.";
  const estimateGap =
    !isMestimateLoading && !hasMestimateError && estimateValue != null && Number.isFinite(car.price)
      ? car.price - estimateValue
      : null;
  const vehicleHistoryHref =
    getAbsoluteHttpUrl(car.description) ||
    (car.vin ? `https://www.carfax.com/VehicleHistory/p/Report.cfx?vin=${car.vin}` : null);
  const lastSearchZip = (() => {
    try {
      const lastFilters = JSON.parse(localStorage.getItem("lastFilters") || "{}");
      if (lastFilters?.zip) return String(lastFilters.zip);
    } catch {
    }

    try {
      const lastSearch = localStorage.getItem("lastSearchURL");
      if (lastSearch) return new URL(lastSearch, window.location.origin).searchParams.get("zip") || "";
    } catch {
    }

    return "";
  })();
  const similarSearchParams = new URLSearchParams({
    make: car.make || "",
    model: car.model || "",
  });
  const similarZip = car.dealerZip || car.dealer?.zip || lastSearchZip;
  if (similarZip) {
    similarSearchParams.set("zip", similarZip);
    similarSearchParams.set("radius", "25");
  }
  const similarSearchHref = `/search?${similarSearchParams.toString()}`;

  return (
    <>
      <Helmet defer={false}>
        <title>{`${car.year} ${car.make} ${car.model} ${car.trim ?? ""}`.trim()} | Meshum</title>
        <meta
          name="description"
          content={metaDescription}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta
          property="og:title"
          content={`${car.year} ${car.make} ${car.model}${car.trim ? " " + car.trim : ""} | Meshum`}
        />
        <meta
          property="og:description"
          content={[
            "Compare this listing with recent sales. Get pricing insights and make smarter decisions.",
            car.exteriorColor ? ` Exterior: ${car.exteriorColor}.` : "",
            car.interiorColor ? ` Interior: ${car.interiorColor}.` : "",
          ].join("")}
        />
        <meta
          property="og:image"
          content={car.images?.[0] || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg"}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${car.year} ${car.make} ${car.model}${car.trim ? " " + car.trim : ""} | Meshum`}
        />
        <meta
          name="twitter:description"
          content={[
            "Explore this used car with price history, comps, and dealer details on Meshum.",
            car.exteriorColor ? ` Exterior: ${car.exteriorColor}.` : "",
            car.interiorColor ? ` Interior: ${car.interiorColor}.` : "",
          ].join("")}
        />
        <meta
          name="twitter:image"
          content={car.images?.[0] || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg"}
        />
        <meta name="twitter:url" content={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${car.year} ${car.make} ${car.model} ${car.trim ?? ""}`.trim(),
            description:
              `${fmtInt(car.mileage)} miles, ${car.transmission}, ${car.fuel}` +
              (car.exteriorColor ? `, Exterior: ${car.exteriorColor}` : "") +
              (car.interiorColor ? `, Interior: ${car.interiorColor}` : ""),
            sku: car.vin,
            mpn: car.vin,
            brand: { "@type": "Brand", name: car.make },
            image: car.images?.[0] || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg",
            ...(car.exteriorColor ? { color: car.exteriorColor } : {}),
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: typeof car.price === "number" ? Number(car.price.toFixed(2)) : car.price,
              itemCondition: "https://schema.org/UsedCondition",
              availability: car.soldDate ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
              url: canonicalUrl,
              seller: { "@type": "Organization", name: car.dealerName || "Private Seller" },
            },
            additionalProperty: [
              ...(car.exteriorColor
                ? [{ "@type": "PropertyValue", name: "Exterior color", value: car.exteriorColor }]
                : []),
              ...(car.interiorColor
                ? [{ "@type": "PropertyValue", name: "Interior color", value: car.interiorColor }]
                : []),
            ],
          })}
        </script>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-4 py-5 sm:px-6 lg:py-8">
        <div className="space-y-5">
          <Link
            to={backHref}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to search
          </Link>

          <div className="lg:hidden space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              {title}
            </h1>
            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
              {isSold && <SoldBadge />}
              <InfoPill>{fmtInt(car.mileage)} miles</InfoPill>
              {dealerLocation && <InfoPill>{dealerLocation}</InfoPill>}
              {car.vin && <InfoPill>VIN {String(car.vin).slice(-8)}</InfoPill>}
            </div>
          </div>

          <section className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.8fr)]">
            <aside className="order-2 space-y-4 lg:order-2 lg:sticky lg:top-4 lg:self-start">
              <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="hidden border-b border-slate-200 px-5 py-4 lg:block">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Used vehicle marketplace
                  </p>
                  <div role="heading" aria-level="1" className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                    {title}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                    {isSold && <SoldBadge />}
                    <InfoPill>{fmtInt(car.mileage)} miles</InfoPill>
                    {dealerLocation && <InfoPill>{dealerLocation}</InfoPill>}
                    {car.vin && <InfoPill>VIN {String(car.vin).slice(-8)}</InfoPill>}
                  </div>
                </div>

                <div className="space-y-4 px-5 py-5">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Asking price
                      </div>
                      <div className="mt-1 text-4xl font-semibold tracking-tight text-slate-950">
                        {usd(car.price, 0)}
                      </div>
                      {isSold && (
                        <div className="mt-1 text-sm font-medium text-rose-700">
                          Sold listing - last listed price
                        </div>
                      )}
                    </div>
                    {!isSold && (
                      <div className="text-right">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Meshum Estimate
                        </div>
                        <div className="mt-1 text-2xl font-semibold text-blue-700">
                          {isMestimateLoading ? (
                            <span className="inline-flex items-center justify-end gap-2 text-base font-semibold text-slate-600 sm:text-lg">
                              <span
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
                              />
                              <span>Loading estimate...</span>
                            </span>
                          ) : hasMestimateError ? (
                            "Estimate unavailable"
                          ) : typeof estimateValue === "number" ? (
                            usd(estimateValue, 0)
                          ) : (
                            "Unavailable"
                          )}
                        </div>
                        {hasMestimateError && (
                          <div className="mt-1 text-sm font-medium text-slate-500">
                            Refresh to try again.
                          </div>
                        )}
                        {estimateGap != null && (
                          <div
                            className={`mt-1 text-sm font-medium ${
                              estimateGap > 0
                                ? "text-rose-700"
                                : estimateGap < 0
                                  ? "text-emerald-700"
                                  : "text-slate-600"
                            }`}
                          >
                            {estimateGap > 0
                              ? `${usd(Math.abs(estimateGap), 0)} above estimate`
                              : estimateGap < 0
                                ? `${usd(Math.abs(estimateGap), 0)} below estimate`
                                : "At estimate"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Dealer</div>
                        <div className="mt-1 text-sm text-slate-600">
                          {car.dealerId ? (
                            <a href={`/dealer/${car.dealerId}`} className="font-medium text-blue-700 hover:text-blue-800">
                              {car.dealerName || "View dealer"}
                            </a>
                          ) : (
                            <span className="font-medium text-slate-800">{car.dealerName || "Private Seller"}</span>
                          )}
                        </div>
                        {dealerLocation && <div className="mt-1 text-sm text-slate-600">{dealerLocation}</div>}
                      </div>
                      {!isSold && car.dealerPhone && (
                        <a
                          href={`tel:${car.dealerPhone}`}
                          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                        >
                          {formatPhoneNumber(car.dealerPhone)}
                        </a>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {vehicleHistoryHref && (
                        <a
                          href={vehicleHistoryHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center"
                          title="View vehicle history report"
                        >
                          <img
                            src="https://meshumimages.blob.core.windows.net/websiteimages/CARFAX_Logo.svg.png"
                            alt="Vehicle history report"
                            className="h-6"
                          />
                        </a>
                      )}
                      {car.soldDate && car.insertDate && (
                        <span className="rounded-full bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700">
                          Sold {new Date(car.soldDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {isSold ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                      This vehicle is marked sold. Dealer contact tools are hidden, but photos, price history, and dealer details remain available for reference.
                    </div>
                  ) : (
                    <ContactAtMestimate
                      key={car.id}
                      dealerId={car.dealerId}
                      carId={car.id}
                      year={car.year}
                      make={car.make}
                      model={car.model}
                      mestimate={estimateValue}
                      askingPrice={car.price}
                      defaultBuyer={{ name: user?.name, email: user?.email, phone: user?.phone }}
                      defaultMessage={`I'm interested in the ${car.year} ${car.make} ${car.model}.`}
                    />
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleShare}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                      aria-label="Share"
                      title="Share"
                    >
                      <FaShareAlt />
                    </button>
                    <button
                      onClick={toggleSave}
                      aria-label={saved ? "Unsave" : "Save"}
                      title={saved ? "Unsave" : "Save"}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white hover:bg-slate-100"
                      style={{ color: saved ? "#dc2626" : "#2563eb" }}
                    >
                      {saved ? <FaHeart /> : <FaRegHeart />}
                    </button>
                    {!isSold && (
                      <button
                        type="button"
                        disabled={comparablesLoading}
                        onClick={() => {
                          if (comparablesLoading) return;
                          setShowCompModal(true);
                          logCompsOpen(car.id);
                        }}
                        className={`inline-flex rounded-xl border px-3 py-2 text-sm font-medium ${
                          comparablesLoading
                            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                            : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        }`}
                      >
                        {comparablesLoading ? "Loading comps..." : "View sold comps"}
                      </button>
                    )}
                    {isSold && (
                      <Link
                        to={similarSearchHref}
                        className="inline-flex rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                      >
                        Find similar active cars
                      </Link>
                    )}
                  </div>

                  {car.soldDate && car.insertDate && (
                    <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      Sold on {new Date(car.soldDate).toLocaleDateString()} - Time on Market:{" "}
                      {Math.ceil((new Date(car.soldDate) - new Date(car.insertDate)) / (1000 * 60 * 60 * 24))} days
                    </div>
                  )}
                </div>
              </section>
            </aside>

            <div className="order-1 min-w-0 space-y-4 lg:order-1">
              <CarImageCarousel imageUrls={car.images} vehicleName={title} desktopHeightClass="md:h-[30rem]" />
            </div>
          </section>

          <section className="space-y-5">
            {!isSold && (
              <ExplainThisPrice
                car={{
                  year: car.year,
                  make: car.make,
                  model: car.model,
                  trim: car.trim,
                  mileage: car.mileage,
                  hasAccidentOrDamage: car.hasAccidentOrDamage ?? null,
                }}
                askingPrice={car.price}
                mestimate={estimateValue}
                comps={insightComps}
                loading={isMestimateLoading}
                error={hasMestimateError}
                unavailableMessage={estimateUnavailableMessage}
              />
            )}

            {!isSold && (
            <section
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              aria-busy={comparablesLoading ? "true" : undefined}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Comparable sold vehicles</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Horizontal preview of sold listings that support the market estimate.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={comparablesLoading}
                  onClick={() => {
                    if (comparablesLoading) return;
                    setShowCompModal(true);
                    logCompsOpen(car.id);
                  }}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium ${
                    comparablesLoading
                      ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                      : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  {comparablesLoading ? "Loading comps..." : "View all comps"}
                </button>
              </div>

              {comparablesLoading ? (
                <div className="mt-4 -mx-1 overflow-hidden pb-2" data-testid="comparable-skeletons">
                  <span className="sr-only" role="status">Loading comparable sold vehicles</span>
                  <div className="flex gap-3 px-1" aria-hidden="true">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <article
                        key={index}
                        className="flex w-[230px] flex-none flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm sm:w-[250px]"
                      >
                        <Skeleton className="h-32 w-full rounded-none" />
                        <div className="flex flex-1 flex-col gap-2 p-3">
                          <Skeleton className="h-6 w-24" />
                          <Skeleton className="h-4 w-4/5" />
                          <Skeleton className="h-4 w-3/5" />
                          <div className="mt-1 space-y-2">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ) : compsToCards(comparables).length > 0 ? (
                <div className="mt-4 -mx-1 overflow-x-auto pb-2">
                  <div className="flex gap-3 px-1">
                    {compsToCards(comparables).map((comp) => (
                      <article
                        key={comp.id}
                        className="flex w-[230px] flex-none snap-start flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm sm:w-[250px]"
                      >
                        <div className="relative bg-slate-100">
                          {comp.imageUrl ? (
                            <img
                              src={comp.imageUrl}
                              alt={`${comp.year} ${comp.make} ${comp.model}`}
                              className="h-32 w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-32 items-center justify-center text-sm text-slate-500">
                              No image available
                            </div>
                          )}
                          <span className="absolute left-2 top-2 rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
                            Sold
                          </span>
                        </div>

                        <div className="flex flex-1 flex-col gap-2 p-3">
                          <div className="min-h-[3rem]">
                            <div className="text-lg font-semibold tracking-tight text-slate-950">
                              {usd(comp.price, 0)}
                            </div>
                            <h3 className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">
                              {comp.year ?? "Year"} {comp.make ?? "Make"} {comp.model ?? "Model"} {comp.trim ?? ""}
                            </h3>
                          </div>

                          <div className="space-y-1 text-sm text-slate-600">
                            <div>
                              {comp.mileage != null ? `${Number(comp.mileage).toLocaleString()} miles` : "Mileage unavailable"}
                            </div>
                            <div>
                              {comp.soldDate ? `Sold ${new Date(comp.soldDate).toLocaleDateString()}` : "Sold date unavailable"}
                            </div>
                            <div>
                              {comp.distanceMiles != null
                                ? `${Number(comp.distanceMiles).toFixed(1)} miles away`
                                : comp.location || comp.dealerCity || comp.dealerState
                                  ? [comp.location, comp.dealerCity, comp.dealerState].filter(Boolean).join(", ")
                                  : "Location unavailable"}
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  No sold comparable vehicles available.
                </div>
              )}
            </section>
            )}

            {car.sellerDesc && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Vehicle description</h2>
                <p
                  className={`mt-3 leading-relaxed text-slate-700 ${showFullDescription ? "" : "line-clamp-3"}`}
                  style={{ wordBreak: "break-word" }}
                >
                  {car.sellerDesc}
                </p>
                <button
                  onClick={() => setShowFullDescription((v) => !v)}
                  className="mt-3 text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  {showFullDescription ? "Show less" : "Read more"}
                </button>
              </section>
            )}

            {String(rawFeatures || "").length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <CarFeatureList
                  features={
                    Array.isArray(car.features)
                      ? car.features.filter(Boolean).map((f) => String(f).trim())
                      : String(rawFeatures)
                          .split(/,|\n|•/g)
                          .map((f) => f.trim())
                          .filter(Boolean)
                  }
                />
              </section>
            )}

            {!isSold && estimateValue != null && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Estimate trend</h2>
                <p className="mt-1 text-sm text-slate-500">Compare sold comps against the Meshum Estimate.</p>
                <div className="mt-4">
                  <MestimateChart compsUsed={compsUsed} mestimate={estimateValue} />
                </div>
              </section>
            )}

            {car?.priceHistory?.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-950">Price history</h2>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full max-w-xl border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border-b border-slate-200 px-3 py-2 text-left">Date</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllPrices ? car.priceHistory : car.priceHistory.slice(0, 1)).map((entry, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="px-3 py-2">{new Date(entry.dateChanged).toLocaleDateString()}</td>
                          <td className="px-3 py-2 text-right">{usd(Number(entry.price), 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {car.priceHistory.length > 1 && (
                  <button
                    onClick={() => setShowAllPrices((prev) => !prev)}
                    className="mt-3 text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    {showAllPrices ? "View less" : "View more"}
                  </button>
                )}
              </section>
            )}
          </section>

          <section className="mt-2">
            <BestDealsNearby
              carId={car.id}
              currentCar={{
                id: car.id,
                imageUrl: car.images?.[0] || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg",
                year: car.year,
                make: car.make,
                model: car.model,
                trim: car.trim,
                price: car.price,
                mileage: car.mileage,
                mestimate: estimateValue,
                dealerName: car.dealerName,
                dealerCity: car.dealerCity,
                dealerState: car.dealerState,
              }}
            />
          </section>

          <section className="mt-2">
            <RecentlyViewed cars={JSON.parse(localStorage.getItem("recentlyViewedCars") || "[]")} />
          </section>
        </div>
      </div>

      {showCompModal && (
        <CompModal
          isLoggedIn={isLoggedIn}
          compsUsed={compsUsed}
          mestimate={estimateValue}
          carId={car.id}
          sourcePage={location.state?.from?.pathname === '/insights/best-deals' ? 'best_deals' : undefined}
          onClose={() => setShowCompModal(false)}
        />
      )}

      {showToast && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-slate-900 px-3 py-2 text-sm text-white shadow-lg">
          {showToast}
        </div>
      )}
    </>
  );
}

function InfoPill({ children }) {
  return <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">{children}</span>;
}

function SoldBadge() {
  return <span className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white">Sold</span>;
}

function compsToCards(comparables = []) {
  return (comparables || []).filter(Boolean);
}

export default CarDetail;
